import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { capitalize, clamp } from "lodash";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";
import { useEventListener, useLocalStorage } from "usehooks-ts";

import { Select } from "src/base-components/Select";
import { PreviewQueryResponse } from "src/clients/features-control";
import { Callout } from "src/design-system/Callout";
import { useEntitySchemas } from "src/entities/queries";
import { FeatureQueryPreview } from "src/featureCatalogue/Queries/Create/FeatureQueryPreview";
import { FeatureSection } from "src/featureCatalogue/Queries/Create/FeatureSection";
import {
  QuerySection,
  SectionHeader,
} from "src/featureCatalogue/Queries/Create/QuerySection";
import {
  ColumnExistsValidationContext,
  defaultFormValues,
  QueryFormValues,
  schema,
} from "src/featureCatalogue/Queries/Create/schema";
import { useFeatureAutoMapping } from "src/featureCatalogue/Queries/Create/useFeatureAutoMapping";
import { useFeatureQueryPreview } from "src/featureCatalogue/queries";
import { Query } from "src/featureCatalogue/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";

const MIN_LEFT_PERCENT = 35;
const MAX_LEFT_PERCENT = 60;
const DEFAULT_LEFT_PERCENT = 40;

type ManagedQuery = Query | Omit<Query, "id"> | undefined;

type FeatureQueryFormProps = {
  query?: ManagedQuery;
  readOnly?: boolean;
  onSubmit: (data: QueryFormValues) => void;
  onValidityChange: (isValid: boolean) => void;
};

export type FeatureQueryFormRef = {
  submit: () => void;
};

const getInitialQueryValue = (query: ManagedQuery): ManagedQuery => {
  if (!query?.window_hop) {
    return query;
  }
  return {
    ...query,
    window_hop: query.window_hop < 60 ? 60 : query.window_hop,
  };
};

export const FeatureQueryForm = forwardRef<
  FeatureQueryFormRef,
  FeatureQueryFormProps
>(({ query, readOnly = false, onSubmit, onValidityChange }, ref) => {
  const { workspace } = useWorkspaceContext();
  const featureQueryPreview = useFeatureQueryPreview();
  const { data: entitiesData, isLoading: entitiesLoading } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  const entities = entitiesData?.entities || [];

  const form = useForm<QueryFormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    values: getInitialQueryValue(query) as QueryFormValues,
    defaultValues: defaultFormValues,
    disabled: readOnly,
    resolver: async (...args) => {
      // Ignore context, we pass it in manually
      const [data, _, options] = args;

      const response: PreviewQueryResponse["data"] | undefined =
        featureQueryPreview.previewData?.data;

      return yupResolver(schema)(
        data,
        {
          availableColumns: Object.keys(response?.at(0) ?? {}),
          values: data,
        } satisfies ColumnExistsValidationContext,
        options,
      );
    },
  });

  const {
    handleFeatureGeneration,
    columnTypes,
    setColumnType,
    reset: resetAutoMapping,
  } = useFeatureAutoMapping(form);
  const selectedEntity = form.watch("columns.entities.0.entity_name");
  const currentQuery = form.watch("query");

  const { isValid } = form.formState;
  const currentWindowLength = form.watch("window_length");

  const isPreviewSettled =
    !featureQueryPreview.isIdle &&
    !featureQueryPreview.isPending &&
    !featureQueryPreview.error &&
    featureQueryPreview.variables?.queryString === currentQuery &&
    featureQueryPreview.variables?.windowLength === currentWindowLength;

  useEffect(() => {
    onValidityChange(isValid && isPreviewSettled);
  }, [isValid, isPreviewSettled, onValidityChange]);

  // Exposes submit() so the Deploy button in the parent page header can trigger
  useImperativeHandle(ref, () => ({
    submit: () => {
      form.handleSubmit((data) => onSubmit(data))();
    },
  }));

  const resetFormState = () => {
    form.setValue("query", "");
    form.setValue("columns.features", []);
    form.setValue("columns.entities.0.column_name", "");
    form.setValue("columns.grouping_keys", []);
    form.resetField("window_length");
    form.resetField("window_hop");
    featureQueryPreview.reset();
    resetAutoMapping();
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPercent, setLeftPercent] = useLocalStorage<number>(
    "fq-creation-left-pane-width",
    DEFAULT_LEFT_PERCENT,
  );
  const clampedLeftPercent = clamp(
    leftPercent,
    MIN_LEFT_PERCENT,
    MAX_LEFT_PERCENT,
  );
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEventListener("mousemove", (event: MouseEvent) => {
    if (!isResizing) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const percent = ((event.clientX - rect.left) / rect.width) * 100;
    setLeftPercent(clamp(percent, MIN_LEFT_PERCENT, MAX_LEFT_PERCENT));
  });

  useEventListener("mouseup", () => {
    if (!isResizing) return;
    setIsResizing(false);
  });

  return (
    <form className="h-full" onSubmit={(e) => e.preventDefault()}>
      <FormProvider {...form}>
        <div className="flex h-full min-h-0 flex-col">
          <div
            ref={containerRef}
            className={twJoin(
              "flex min-h-0 flex-1",
              isResizing && "cursor-col-resize select-none",
            )}
          >
            {/* Left column - scrollable accordions */}
            <div
              className="decideScrollbar flex min-w-0 flex-col gap-y-4 overflow-y-auto border-gray-200 border-r p-6"
              style={{ width: `${clampedLeftPercent}%` }}
            >
              {/* Pick Entity Section */}
              <div className="border-gray-200 border-b pb-4">
                <SectionHeader title="Pick an Entity" />
                <Controller
                  control={form.control}
                  name="columns.entities.0.entity_name"
                  render={({ field }) => (
                    <Select
                      dataLoc="entity-picker"
                      disabled={entitiesLoading || readOnly}
                      options={
                        entities?.map((entity) => ({
                          key: entity._id,
                          value: capitalize(entity._display_name_plural),
                        })) ?? []
                      }
                      placeholder="Select entity"
                      value={field.value ?? null}
                      onChange={(value) => {
                        // We clear/reset the form when the entity change.
                        field.onChange(value);
                        resetFormState();
                      }}
                    />
                  )}
                />
              </div>

              {/* SQL Query Section */}
              {selectedEntity && (
                <QuerySection
                  featureQueryPreview={featureQueryPreview}
                  handleFeatureGeneration={handleFeatureGeneration}
                  readOnly={readOnly}
                />
              )}

              {form.formState.errors.columns?.root?.message && (
                <div className="pb-4">
                  <Callout type="error">
                    {form.formState.errors.columns?.root?.message}
                  </Callout>
                </div>
              )}

              {/* Features Section */}
              {((query && "id" in query) ||
                (featureQueryPreview.previewData?.data &&
                  featureQueryPreview.previewData.data.length > 0 &&
                  Object.keys(featureQueryPreview.previewData.data[0] ?? {})
                    .length >= 2 &&
                  !featureQueryPreview.error)) && (
                <FeatureSection
                  columnTypes={columnTypes}
                  featureQueryPreview={featureQueryPreview}
                  readonly={readOnly}
                />
              )}
            </div>

            {/* Resize handle */}
            <button
              aria-label="Resize pane"
              className={twJoin(
                "w-1 shrink-0 cursor-col-resize bg-indigo-500 opacity-0 transition-opacity duration-150 hover:opacity-100",
                isResizing && "opacity-100",
              )}
              type="button"
              onMouseDown={handleResizeStart}
            />

            {/* Right column - preview */}
            <div className="min-h-0 min-w-0 flex-1 overflow-hidden pt-3 pl-5">
              {/* Preview section */}
              <FeatureQueryPreview
                columnTypes={columnTypes}
                previewMutation={featureQueryPreview}
                onColumnTypeChange={setColumnType}
              />
            </div>
          </div>
        </div>
      </FormProvider>
    </form>
  );
});
